import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext'; 
import CheckoutCouponModule from './CheckoutCouponModule.jsx'; // Add .jsx explicitly if named that way
const CheckoutForm = () => {
  const navigate = useNavigate();
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);

  // Shipping Address Form State
  const [shippingAddress, setShippingAddress] = useState({
    fullName: '', 
    phone: '',
    street: '',
    city: '',
    state: '',
    pincode: ''
  });

  // 🌟 DYNAMIC PRICE MARKDOWN COUPLING STATES
  const { cart, cartItems, clearCart } = useCart();
  const activeCart = cart || cartItems || [];
  
  const rawTotal = activeCart.reduce((sum, item) => {
    const itemPrice = item.discountPrice || item.price || 0;
    return sum + (itemPrice * (item.quantity || 1));
  }, 0);

  const [discountAmount, setDiscountAmount] = useState(0);
  const [payableTotal, setPayableTotal] = useState(rawTotal);
  const [appliedCouponCode, setAppliedCouponCode] = useState('');

  const handleAddressChange = (e) => {
    setShippingAddress({ ...shippingAddress, [e.target.name]: e.target.value });
  };

  // Callback to synchronize state metrics when coupon is processed
  const handleCouponStateUpdate = (savedCash, finalBill, activeCode) => {
    setDiscountAmount(savedCash);
    setPayableTotal(finalBill); // Passes structural markdown price down to state controllers
    setAppliedCouponCode(activeCode);
  };

  const handleRazorpayPayment = async () => {
    if (!activeCart || activeCart.length === 0) {
      setErrorMessage("Cannot checkout an empty shopping bag. Head back to the gallery and add items first!");
      return;
    }

    if (!shippingAddress.street || !shippingAddress.city || !shippingAddress.state || !shippingAddress.pincode || !shippingAddress.fullName || !shippingAddress.phone) {
      setErrorMessage("⚠️ All physical address and recipient properties must be complete to chart logistics delivery paths.");
      return;
    }

    setIsProcessing(true);
    setErrorMessage('');

    try {
      // 🌟 UPDATED PATH: Pass the coupon-adjusted payableTotal to the backend order creator
      const response = await fetch('https://api.modischekledings.com/api/payment/create-order', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          cartItems: activeCart,
          amount: payableTotal // Securely transmits the markdown final price 
        }),
      });

      const orderData = await response.json();

      if (!orderData.success) {
        throw new Error(orderData.message || 'Failed initializing secure order session.');
      }

      const paymentOptions = {
        key: orderData.keyId,
        amount: orderData.amount, // Synchronized to coupon rate inside native popup
        currency: 'INR',
        name: 'Modische Kledings',
        description: appliedCouponCode ? `Showroom Checkout - Code: ${appliedCouponCode}` : 'Showroom Catalogue Checkout',
        image: 'https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?q=80&w=100&auto=format&fit=crop',
        order_id: orderData.orderId,
        
        handler: async function (response) {
          try {
            const verifyResponse = await fetch('https://api.modischekledings.com/api/payment/verify-payment', {
              method: 'POST',
              headers: { 
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('modische_token')}`
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
                cartItems: activeCart, 
                totalAmount: payableTotal // Verify matching validation structures
              }),
            });

            const verificationResult = await verifyResponse.json();

            if (verificationResult.success) {
              await fetch('https://api.modischekledings.com/api/orders/create-order', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${localStorage.getItem('modische_token')}`
                },
                body: JSON.stringify({
                  orderId: response.razorpay_order_id,
                  paymentId: response.razorpay_payment_id,
                  totalAmount: payableTotal, // Record coupon final total inside customer history logs
                  shippingAddress, 
                  couponApplied: appliedCouponCode || null,
                  discountValue: discountAmount,
                  items: activeCart.map(item => ({
                    product: item._id || item.id,
                    name: item.name,
                    size: item.size,
                    quantity: item.quantity,
                    price: item.discountPrice || item.price
                  }))
                })
              });

              setPaymentSuccess(true);
              if (clearCart) clearCart(); 
              setTimeout(() => navigate('/my-orders'), 4000); 
            } else {
              setErrorMessage(verificationResult.message || 'Signature security checkpoint failed.');
            }

          } catch (error) {
            console.error("Verification connection error:", error);
            setErrorMessage('Network timeout connecting with order processing servers.');
          }
        },
        prefill: {
          name: shippingAddress.fullName,
          email: JSON.parse(localStorage.getItem('modische_user'))?.email || '',
          contact: shippingAddress.phone,
        },
        theme: {
          color: '#2C1E12',
        },
      };

      const razorpayWindowInstance = new window.Razorpay(paymentOptions);
      razorpayWindowInstance.open();

    } catch (err) {
      console.error("Checkout break:", err);
      setErrorMessage(err.message || 'Could not connect to database servers.');
    } finally {
      setIsProcessing(false);
    }
  };

  if (paymentSuccess) {
    return (
      <div className="min-h-[85vh] flex flex-col items-center justify-center text-center px-6 bg-[#F5F5DC]">
        <div className="w-24 h-24 mb-6 relative">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
            <circle className="stroke-[#2C1E12] fill-none" cx="26" cy="26" r="25" strokeWidth="3" style={{ strokeDasharray: 166, strokeDashoffset: 166, animation: 'strokeCircle 0.6s cubic-bezier(0.65, 0, 0.45, 1) forwards' }} />
            <path className="stroke-[#2C1E12] fill-none" strokeWidth="3" strokeLinecap="round" d="M14.1 27.2l7.1 7.2 16.7-16.8" style={{ strokeDasharray: 48, strokeDashoffset: 48, animation: 'strokeCheck 0.3s cubic-bezier(0.65, 0, 0.45, 1) 0.6s forwards' }} />
          </svg>
        </div>
        <h2 className="text-3xl font-serif uppercase tracking-wider text-[#2C1E12] mb-3 animate-fade-in">Order Confirmed</h2>
        <p className="text-[10px] uppercase tracking-[0.25em] text-[#664229] opacity-80 max-w-sm leading-relaxed">Transaction authenticated successfully. Redirecting to showroom...</p>
      </div>
    );
  }

  return (
    <div className="min-h-[85vh] bg-[#D4C5B9]/30 flex items-center justify-center p-4 pt-32 pb-20">
      <div className="w-full max-w-5xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row border border-zinc-200">
        
        {/* LEFT COLUMN: BRAND DETAILS PANEL */}
        <div className="w-full md:w-[42%] bg-[#2C1E12] p-8 flex flex-col justify-between relative text-left">
          <div className="space-y-6">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-10 h-10 bg-zinc-800 rounded-xl flex items-center justify-center font-serif text-lg text-[#F5F5DC] font-black">M</div>
              <h3 className="text-lg font-serif font-bold text-[#F5F5DC] tracking-wide">Modische Kledings</h3>
            </div>

            {/* DYNAMIC BREAKDOWN DISPLAY CARD */}
            <div className="bg-[#FAF9F6] text-[#2C1E12] p-6 rounded-xl border border-[#E5D3B3]/20 shadow-inner space-y-2">
              <div className="flex justify-between items-center text-[10px] uppercase font-mono text-zinc-400">
                <span>Bag Subtotal:</span>
                <span className="font-sans font-bold">₹{rawTotal}</span>
              </div>
              {discountAmount > 0 && (
                <div className="flex justify-between items-center text-[10px] uppercase font-mono text-green-700 font-bold">
                  <span>Coupon ({appliedCouponCode}):</span>
                  <span>- ₹{discountAmount}</span>
                </div>
              )}
              <div className="border-t border-zinc-100 pt-2">
                <p className="text-[10px] font-sans font-bold uppercase tracking-wider text-zinc-400 mb-0.5">Final Payable Amount</p>
                <div className="text-3xl font-serif font-black tracking-tight text-[#2C1E12]">₹{payableTotal}</div>
              </div>
            </div>

            <div className="bg-zinc-900/40 border border-zinc-800 p-4 rounded-xl text-[#E5D3B3] space-y-1">
              <p className="text-[9px] uppercase tracking-wider text-zinc-400 font-mono">Fulfillment Target:</p>
              <p className="font-mono text-xs text-zinc-200 font-bold">{shippingAddress.fullName || 'No Recipient Added'}</p>
              <p className="font-mono text-[11px] text-zinc-400">{shippingAddress.phone}</p>
            </div>
          </div>

          <div className="mt-16 opacity-20 transform translate-y-4 pointer-events-none hidden md:block">
            <div className="w-32 h-20 bg-zinc-700 rounded-lg skew-x-12 mb-2" />
            <div className="w-40 h-12 bg-zinc-600 rounded-lg -skew-x-12" />
            <p className="text-[9px] text-zinc-400 font-mono tracking-widest uppercase mt-4">Secured by Razorpay x Delhivery</p>
          </div>
        </div>

        {/* RIGHT COLUMN: DYNAMIC DELIVERY ADDRESS INPUT LAYER & COUPONS */}
        <div className="flex-1 bg-white p-8 sm:p-12 flex flex-col justify-between text-left">
          <div>
            <div className="flex justify-between items-center pb-4 border-b border-zinc-100 mb-6">
              <h2 className="text-xl font-serif text-zinc-800 tracking-wide">Shipping Details</h2>
              <button onClick={() => navigate('/cart')} className="text-xs text-zinc-400 hover:text-zinc-900 flex items-center space-x-1 transition font-medium bg-transparent border-none cursor-pointer">
                <span>← Back to Bag</span>
              </button>
            </div>

            {errorMessage && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-xs rounded-xl flex items-center space-x-2 font-mono">
                <span className="cursor-pointer font-bold" onClick={() => setErrorMessage('')}>✕</span>
                <span>{errorMessage}</span>
              </div>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-[#664229] font-bold mb-1.5">Recipient Full Name</label>
                  <input type="text" name="fullName" value={shippingAddress.fullName} onChange={handleAddressChange} placeholder="Shubham ..."
                    className="w-full p-3 bg-zinc-50 border border-zinc-200 text-xs text-zinc-800 focus:outline-none focus:border-[#2C1E12] rounded-md transition" />
                </div>
                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-[#664229] font-bold mb-1.5">Contact Phone</label>
                  <input type="text" name="phone" value={shippingAddress.phone} onChange={handleAddressChange} placeholder="91XXXXXXXX"
                    className="w-full p-3 bg-zinc-50 border border-zinc-200 text-xs text-zinc-800 focus:outline-none focus:border-[#2C1E12] rounded-md transition" />
                </div>
              </div>

              <div>
                <label className="block text-[9px] uppercase tracking-widest text-[#664229] font-bold mb-1.5">Apartment / Suite / Street Address</label>
                <input type="text" name="street" value={shippingAddress.street} onChange={handleAddressChange} placeholder="e.g., House No. 402, 4th Floor, Saket Nagar"
                  className="w-full p-3 bg-zinc-50 border border-zinc-200 text-xs text-zinc-800 focus:outline-none focus:border-[#2C1E12] rounded-md transition" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-[#664229] font-bold mb-1.5">City</label>
                  <input type="text" name="city" value={shippingAddress.city} onChange={handleAddressChange} placeholder="e.g., Indore"
                    className="w-full p-3 bg-zinc-50 border border-zinc-200 text-xs text-zinc-800 focus:outline-none focus:border-[#2C1E12] rounded-md transition" />
                </div>
                <div>
                  <label className="block text-[9px] uppercase tracking-widest text-[#664229] font-bold mb-1.5">State</label>
                  <input type="text" name="state" value={shippingAddress.state} onChange={handleAddressChange} placeholder="e.g., Madhya Pradesh"
                    className="w-full p-3 bg-zinc-50 border border-zinc-200 text-xs text-zinc-800 focus:outline-none focus:border-[#2C1E12] rounded-md transition" />
                </div>
              </div>

              <div>
                <label className="block text-[9px] uppercase tracking-widest text-[#664229] font-bold mb-1.5">Pincode / Postal Code (6 Digits)</label>
                <input type="text" maxLength="6" name="pincode" value={shippingAddress.pincode} onChange={handleAddressChange} placeholder="e.g., 452001"
                  className="w-full p-3 bg-zinc-50 border border-zinc-200 text-xs font-mono tracking-widest text-zinc-800 focus:outline-none focus:border-[#2C1E12] rounded-md transition" />
              </div>
            </div>

            {/* 🌟 PROMO VOUCHERS SUBMISSION DRAWER LAYER */}
            <div className="mt-4">
              <CheckoutCouponModule 
                cartTotal={rawTotal} 
                onCouponApplied={handleCouponStateUpdate} 
              />
            </div>
          </div>

          <div className="pt-6 border-t border-zinc-100 mt-6">
            <button
              onClick={handleRazorpayPayment}
              disabled={isProcessing}
              className="w-full py-4 bg-[#2C1E12] text-[#F5F5DC] rounded-xl text-xs font-bold uppercase tracking-widest shadow-xl hover:opacity-90 transition-all disabled:opacity-40 cursor-pointer border-none"
            >
              {isProcessing ? 'Mounting Gateway Engine...' : 'Proceed to Secure Payment'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default CheckoutForm;